<template>
  <v-dialog persistent
            scrollable
            v-bind:fullscreen="isMobile"
            v-bind:value="value"
            v-bind:width="640"
            v-on:input="$emit('input', $event)">
    <v-card class="sf-crop">
      <v-card-title class="sf-crop__title">
        <span>{{ title || $t('title') }}</span>
        <v-spacer />
        <v-btn icon
               v-on:click="cancel">
          <v-icon>ti-x</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="sf-crop__body">
        <p class="sf-crop__hint mb-3">{{ hint || $t('hint') }}</p>
        <div class="sf-crop__stage">
          <img alt=""
               class="sf-crop__image"
               ref="image"
               v-bind:src="objectUrl"
               v-if="objectUrl != null && error == null">
          <div class="sf-crop__loading"
               v-if="loading">
            <v-progress-circular color="primary"
                                 indeterminate />
          </div>
        </div>
        <v-alert class="mt-4 mb-0"
                 text
                 type="error"
                 v-if="error != null">
          {{ error }}
        </v-alert>
        <div class="sf-crop__tools mt-3"
             v-if="ready && error == null">
          <v-btn icon
                 v-bind:title="$t('zoomOut')"
                 v-on:click="zoom(-0.1)">
            <v-icon>ti-zoom-out</v-icon>
          </v-btn>
          <v-btn icon
                 v-bind:title="$t('zoomIn')"
                 v-on:click="zoom(0.1)">
            <v-icon>ti-zoom-in</v-icon>
          </v-btn>
          <v-btn icon
                 v-bind:title="$t('rotate')"
                 v-on:click="rotate">
            <v-icon>ti-rotate-clockwise</v-icon>
          </v-btn>
          <v-btn icon
                 v-bind:title="$t('reset')"
                 v-on:click="reset">
            <v-icon>ti-refresh</v-icon>
          </v-btn>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text
               v-on:click="cancel">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn color="primary"
               depressed
               v-bind:disabled="!ready || error != null || exporting"
               v-bind:loading="exporting"
               v-on:click="confirm">
          <v-icon left>ti-check</v-icon>
          {{ $t('apply') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  /**
   * Zuschneidedialog auf Basis von Cropper.js 1.x (MIT, Vanilla JS). Rahmen mit festem Seitenverhältnis,
   * Bild wird per Maus/Touch verschoben und gezoomt. Das Ergebnis wird im Browser auf `outputSize` skaliert
   * und als Blob (Standard JPEG) ausgegeben – der Server braucht keine Bildbibliothek.
   * Die Bibliothek wird erst beim Öffnen nachgeladen (eigener Chunk).
   */
  export default {
    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.smAndDown
      }
    },
    data: () => ({
      cropper: null,
      error: null,
      exporting: false,
      loading: false,
      objectUrl: null,
      ready: false
    }),
    beforeDestroy() {
      this.destroy()
    },
    methods: {
      cancel() {
        this.$emit('input', false)
        this.$emit('cancel')
      },
      async confirm() {
        if (this.cropper == null) {
          return
        }

        this.exporting = true

        try {
          const canvas = this.cropper.getCroppedCanvas({
            width: this.outputSize,
            height: Math.round(this.outputSize / this.aspectRatio),
            fillColor: '#fff',
            imageSmoothingEnabled: true,
            imageSmoothingQuality: 'high'
          })

          const blob = await new Promise((resolve, reject) => {
            canvas.toBlob(b => b != null ? resolve(b) : reject(new Error('toBlob')), this.mimeType, this.quality)
          })

          this.$emit('input', false)
          this.$emit('confirm', blob)
        } catch (e) {
          this.error = this.$t('exportFailed')
        } finally {
          this.exporting = false
        }
      },
      destroy() {
        if (this.cropper != null) {
          this.cropper.destroy()
          this.cropper = null
        }

        if (this.objectUrl != null) {
          URL.revokeObjectURL(this.objectUrl)
          this.objectUrl = null
        }

        this.ready = false
      },
      async load() {
        this.destroy()
        this.error = null

        if (this.file == null) {
          return
        }

        this.loading = true

        try {
          const [ { default: Cropper } ] = await Promise.all([
            import(/* webpackChunkName: "image-crop" */ 'cropperjs'),
            import(/* webpackChunkName: "image-crop" */ 'cropperjs/dist/cropper.css')
          ])

          const dimensions = await this.readDimensions(this.file)

          if (dimensions == null) {
            this.error = this.$t('unsupportedFormat')
            return
          }

          if (Math.min(dimensions.width, dimensions.height) < this.minEdge) {
            this.error = this.$t('tooSmall', { min: this.minEdge, width: dimensions.width, height: dimensions.height })
            return
          }

          this.objectUrl = URL.createObjectURL(this.file)

          await this.$nextTick()

          const image = this.$refs.image

          if (image == null) {
            return
          }

          this.cropper = new Cropper(image, {
            aspectRatio: this.aspectRatio,
            autoCropArea: 1,
            background: false,
            checkOrientation: true,
            dragMode: 'move',
            guides: false,
            responsive: true,
            toggleDragModeOnDblclick: false,
            viewMode: 1,
            ready: () => {
              this.ready = true
              this.loading = false
            }
          })
        } catch (e) {
          this.error = this.$t('unsupportedFormat')
          this.loading = false
        } finally {
          if (this.error != null) {
            this.loading = false
          }
        }
      },
      readDimensions(file) {
        return new Promise(resolve => {
          const url = URL.createObjectURL(file)
          const image = new Image()

          image.onload = () => {
            URL.revokeObjectURL(url)
            resolve({ width: image.naturalWidth, height: image.naturalHeight })
          }
          image.onerror = () => {
            URL.revokeObjectURL(url)
            resolve(null)
          }
          image.src = url
        })
      },
      reset() {
        if (this.cropper != null) {
          this.cropper.reset()
        }
      },
      rotate() {
        if (this.cropper != null) {
          this.cropper.rotate(90)
        }
      },
      zoom(ratio) {
        if (this.cropper != null) {
          this.cropper.zoom(ratio)
        }
      }
    },
    name: 'ImageCropDialog',
    props: {
      aspectRatio: {
        default: 1,
        type: Number
      },
      file: File,
      hint: String,
      mimeType: {
        default: 'image/jpeg',
        type: String
      },
      /** Kürzeste Seite des Originals, unter der der Upload abgelehnt wird. */
      minEdge: {
        default: 400,
        type: Number
      },
      outputSize: {
        default: 1200,
        type: Number
      },
      quality: {
        default: 0.85,
        type: Number
      },
      title: String,
      value: Boolean
    },
    watch: {
      value(open) {
        if (open) {
          this.load()
        } else {
          this.destroy()
        }
      }
    }
  }
</script>

<style scoped>
  .sf-crop__title {
    font-size: 1.125rem;
    font-weight: 600;
  }

  .sf-crop__body {
    display: flex;
    flex-direction: column;
  }

  .sf-crop__hint {
    color: var(--sf-text-secondary);
    font-size: .875rem;
  }

  .sf-crop__stage {
    background: var(--sf-surface-muted, #f3f4f6);
    border-radius: 12px;
    height: 60vh;
    max-height: 520px;
    min-height: 280px;
    overflow: hidden;
    position: relative;
  }

  .sf-crop__image {
    display: block;
    max-width: 100%;
  }

  .sf-crop__loading {
    align-items: center;
    display: flex;
    inset: 0;
    justify-content: center;
    position: absolute;
  }

  .sf-crop__tools {
    display: flex;
    gap: 4px;
    justify-content: center;
  }
</style>

<i18n>
  {
    "de": {
      "apply": "Übernehmen",
      "cancel": "Abbrechen",
      "exportFailed": "Das Bild konnte nicht verarbeitet werden. Bitte versuchen Sie es mit einer anderen Datei.",
      "hint": "Verschieben Sie das Bild und zoomen Sie, bis der gewünschte Ausschnitt im Rahmen liegt. Der Ausschnitt wird quadratisch gespeichert.",
      "reset": "Zurücksetzen",
      "rotate": "Um 90° drehen",
      "title": "Bildausschnitt wählen",
      "tooSmall": "Das Bild ist zu klein ({width} × {height} px). Die kürzere Seite muss mindestens {min} px haben.",
      "unsupportedFormat": "Dieses Dateiformat kann der Browser nicht lesen. Bitte verwenden Sie JPG oder PNG.",
      "zoomIn": "Vergrößern",
      "zoomOut": "Verkleinern"
    },
    "en": {
      "apply": "Apply",
      "cancel": "Cancel",
      "exportFailed": "The image could not be processed. Please try another file.",
      "hint": "Move and zoom the image until the desired area is inside the frame. The selection is saved as a square.",
      "reset": "Reset",
      "rotate": "Rotate 90°",
      "title": "Choose image area",
      "tooSmall": "The image is too small ({width} × {height} px). The shorter side must be at least {min} px.",
      "unsupportedFormat": "The browser cannot read this file format. Please use JPG or PNG.",
      "zoomIn": "Zoom in",
      "zoomOut": "Zoom out"
    }
  }
</i18n>
